// 系列管理弹窗组件
import React, { useState, useMemo } from 'react';
import { X, Plus, Save, Trash2 } from 'lucide-react';
import { supabase } from '@/config/supabase';
import ConfirmDialog from '@/components/ConfirmDialog';
import type { ProductCategory } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';
import type { ProductSeries } from '../hooks/useSeriesManager';

interface SeriesManagerModalProps {
  seriesList: ProductSeries[];
  defaultCategory?: ProductCategory;
  onClose: () => void;
  onRefresh: () => void;
}

export function SeriesManagerModal({
  seriesList,
  defaultCategory,
  onClose,
  onRefresh,
}: SeriesManagerModalProps) {
  const [editingSeries, setEditingSeries] = useState<ProductSeries | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    category: (defaultCategory || 'product') as string,
    description: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; series: ProductSeries | null }>({ open: false, series: null });

  const handleEdit = (series: ProductSeries) => {
    setEditingSeries(series);
    setFormData({
      name: series.name,
      name_en: series.name_en || '',
      category: series.category,
      description: series.description || '',
    });
    setIsCreating(false);
  };

  const handleCreate = () => {
    setEditingSeries(null);
    setFormData({
      name: '',
      name_en: '',
      category: defaultCategory || 'product',
      description: '',
    });
    setIsCreating(true);
  };

  // 在指定分类下创建系列
  const handleCreateForCategory = (category: ProductCategory) => {
    setEditingSeries(null);
    setFormData({
      name: '',
      name_en: '',
      category: category,
      description: '',
    });
    setIsCreating(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    
    setIsSaving(true);
    try {
      if (isCreating) {
        const { error } = await supabase
          .from('ditu_product_series')
          .insert({
            name: formData.name,
            name_en: formData.name_en || null,
            category: formData.category,
            description: formData.description || null,
            is_active: true,
          });
        if (error) throw error;
      } else if (editingSeries) {
        const { error } = await supabase
          .from('ditu_product_series')
          .update({
            name: formData.name,
            name_en: formData.name_en || null,
            category: formData.category,
            description: formData.description || null,
          })
          .eq('id', editingSeries.id);
        if (error) throw error;
      }
      
      onRefresh();
      setEditingSeries(null);
      setIsCreating(false);
      setFormData({ name: '', name_en: '', category: 'product', description: '' });
    } catch (error) {
      console.error('Error saving series:', error);
      alert('保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (series: ProductSeries) => {
    setDeleteConfirm({ open: true, series });
  };

  const doDelete = async () => {
    const series = deleteConfirm.series;
    setDeleteConfirm({ open: false, series: null });
    if (!series) return;
    try {
      const { error } = await supabase
        .from('ditu_product_series')
        .update({ is_active: false })
        .eq('id', series.id);
      if (error) throw error;
      onRefresh();
    } catch (error) {
      console.error('Error deleting series:', error);
    }
  };

  // 按分类分组
  const groupedSeries = useMemo(() => {
    const groups: Record<string, ProductSeries[]> = {
      all_in_one: [],
      product: [],
      module: [],
    };
    seriesList.forEach(s => {
      if (groups[s.category]) {
        groups[s.category].push(s);
      }
    });
    return groups;
  }, [seriesList]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
          <h2 className="text-lg font-semibold text-gray-900">
            系列管理
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(85vh-130px)]">
          {/* Left: Series List */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-4">
            {Object.entries(groupedSeries).map(([category, items]) => (
              <div key={category} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase">
                    {ProductCategoryLabels[category as ProductCategory] || category}
                  </h3>
                  <button
                    onClick={() => handleCreateForCategory(category as ProductCategory)}
                    className="p-1 hover:bg-blue-50 rounded text-blue-500 transition-colors"
                    title={`在${ProductCategoryLabels[category as ProductCategory]}分类下添加系列`}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {items.length === 0 ? (
                  <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-400 mb-2">暂无系列</p>
                    <button
                      onClick={() => handleCreateForCategory(category as ProductCategory)}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      添加第一个系列
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {items.map((series) => (
                      <div
                        key={series.id}
                        className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                          editingSeries?.id === series.id
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleEdit(series)}
                      >
                        <div>
                          <p className="font-medium text-sm">{series.name}</p>
                          {series.description && (
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">
                              {series.description}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(series);
                          }}
                          className="p-1 hover:bg-red-50 rounded text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right: Edit Form */}
          <div className="w-1/2 p-4 overflow-y-auto">
            {(isCreating || editingSeries) ? (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">
                  {isCreating ? '添加系列' : '编辑系列'}
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    系列名称 *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="如: Indoor Professional"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    英文名称
                  </label>
                  <input
                    type="text"
                    value={formData.name_en}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    所属分类 *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all_in_one">一体机</option>
                    <option value="product">产品</option>
                    <option value="module">模组</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="系列描述..."
                  />
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !formData.name.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {isSaving ? '保存中...' : '保存'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingSeries(null);
                      setIsCreating(false);
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    取消
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p>选择一个系列进行编辑，或点击添加创建新系列</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="删除系列"
        message={`确定要删除系列 "${deleteConfirm.series?.name}" 吗？`}
        variant="danger"
        confirmText="删除"
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirm({ open: false, series: null })}
      />
    </div>
  );
}
